import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AxiosError, AxiosResponse } from "axios";
import AdminPageCardTitle from "components/AdminPageCardTitle/AdminPageCardTitle";
import NoRowsTableDataOverlay from "components/NoRowsTableDataOverlay/NoRowsTableDataOverlay";
import { commonStyle } from "pages/Dashboard/common.styles";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AdminUserDetailPersonalDetails,
  AdminUserDetailResponse,
  AdminWalletData,
  AdminWalletResponse,
  TransactionsListData,
  TransactionsResponse,
} from "reduxStore/dataTypes";
import {
  adminWalletDetails,
  fetchAdminTransactions,
  fetchAdminUserDetails,
} from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";
import { walletColumn } from "./dataGridDef";
import { transactionColumn } from "../TransactionPage/dataGridDef";
import { formateTransactionDate } from "utils/dateFormat";
import CopyIcon from "assets/svg/CopyIcon";
import LinkIcon from "@mui/icons-material/Link";

const WalletDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [walletData, setWalletData] = useState<AdminWalletData>();
  const [transactionData, setTransactionData] = useState<
    TransactionsListData[]
  >([]);

  const getWalletDetails = async () => {
    try {
      if (!id) return;
      setLoading(true);
      const body = {
        user_id: id,
      };
      const response: AxiosResponse<AdminWalletResponse> =
        await adminWalletDetails(body);
      if (response && response.status === 200) {
        setWalletData(response?.data?.data[0]);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        //toast.error(error?.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getUserTransactions = async () => {
    try {
      if (!id) return;
      const response: AxiosResponse<TransactionsResponse> =
        await fetchAdminTransactions({ user_id: id });
      if (response && response.status === 200) {
        setTransactionData(response?.data?.data?.transactions);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        //toast.error(error?.response?.data?.message);
      }
    }
  };

  useEffect(() => {
    if (id) {
      getWalletDetails();
      getUserTransactions();
    }
  }, [id]);

  const RenderField = ({ label, value, copyValue, exploreLink }: any) => {
    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
        mt={1}
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <Typography sx={styles.inputLabelStyle}>{label}</Typography>
        <Typography sx={styles.inputValueStyle}>{value}</Typography>
        <Stack direction={"row"} alignItems={"center"}>
          {copyValue ? (
            <IconButton
              onClick={() => {
                navigator.clipboard.writeText(copyValue);
                toast.success("Copied to clipboard");
              }}
              size="small"
            >
              <CopyIcon
                color={theme.palette.primary.main}
                height={18}
                width={18}
              />
            </IconButton>
          ) : null}
          {exploreLink ? (
            <IconButton
              onClick={() => {
                window.open(exploreLink, "_blank", "noopener,noreferrer");
              }}
              size="small"
            >
              <LinkIcon
                sx={{ fontSize: 20, color: theme.palette.primary.main }}
              />
            </IconButton>
          ) : null}
        </Stack>
      </Stack>
    );
  };

  return (
    <Box sx={styles.container}>
      <AdminPageCardTitle
        title={"Wallet Details"}
        icon={<ArrowBackIcon sx={{ color: theme.palette.primary.main }} />}
        onIconClick={() => {
          navigate(-1);
        }}
      />
      <Box sx={{ mt: 3 }} />

      <RenderField
        label={"User Name :"}
        value={`${walletData?.firstName || ""} ${walletData?.lastName || ""}`}
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"Balance :"}
        value={`$${
          walletData?.balance
            ? walletData?.balance + " " + walletData?.cryptoTicker
            : "-"
        }`}
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"Address :"}
        value={`${walletData?.address ? walletData?.address : "-"}`}
        copyValue={walletData?.address !== "-" ? walletData?.address : null}
        exploreLink={
          walletData?.address !== "-"
            ? `${process.env.REACT_APP_EXPLORE_URL}/address/${walletData?.address}`
            : null
        }
      />

      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <RenderField
        label={"Creation Date :"}
        value={`${
          walletData?.createdAt
            ? formateTransactionDate(walletData?.createdAt)
            : "-"
        }`}
      />
      <Divider
        sx={{ borderColor: alpha(theme.palette.text.primary, 0.2), my: 1 }}
      />
      <Typography
        sx={{
          mt: 4,
          mb: -1,
          color: theme.palette.primary.main,
          fontSize: responsiveFontSize(18),
        }}
      >
        Transactions
      </Typography>
      <Box
        sx={{
          mt: 6,
          width: "100%",
          overflowX: { xs: "auto", md: "visible" },
          minWidth: 0,
        }}
      >
        <DataGrid
          rows={transactionData || []}
          columns={transactionColumn.filter(
            (column) => column.field !== "name"
          )}
          getRowId={(row) => row.transactionId}
          loading={loading}
          slots={{
            noRowsOverlay: () => (
              <NoRowsTableDataOverlay title={"No wallet data found"} />
            ),
          }}
          sx={{
            minWidth: 600,
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer",
            },
            fontSize: { xs: 12, sm: 14 },
          }}
          hideFooterPagination
          disableRowSelectionOnClick
          onRowClick={(row) => {
            navigate(`/admin/transactions/${row.row.transactionId}`);
          }}
        />
      </Box>
    </Box>
  );
};
export default WalletDetailsPage;
const styles = {
  container: {
    flexGrow: 1,
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: { xs: 1, sm: 2, md: 3 },
    ml: { xs: 0, md: -5 },
    width: "100%",
    minWidth: 0,
    maxWidth: "100vw",
  },
  inputLabelStyle: {
    fontSize: { xs: responsiveFontSize(12), sm: responsiveFontSize(14) },
    fontWeight: 400,
    color: theme.palette.text.primary,
    width: { xs: "100%", sm: "15em" },
    wordBreak: "break-word",
  },
  inputValueStyle: {
    fontSize: { xs: responsiveFontSize(12), sm: responsiveFontSize(14) },
    fontWeight: 400,
    color: theme.palette.primary.main,
    wordBreak: "break-word",
  },
};
